import type {
  ValidEndpoints
} from '@/api/clients/backend-client'
import type { DirectusUser } from '@directus/sdk'
import { process_number_or_null, type CrudConverter } from './converter-helpers'
import { createItem, deleteItem, fetchCollection, patchMultiple, updateItem } from './helper-functions'

const collectionName: ValidEndpoints = 'locations'
export type Directus_Favourite_Location = {
  id: number | string
  user_created: string | Partial<DirectusUser>
  Coordinates: GeoJSON.Point
  Name: string
  Address: string
  Visibility: 'private' | 'fleet'
  Type: 'Work' | 'Home' | 'Depot' | 'Other'
  Charge_Here: boolean
  Rating?: null | number
  Load_Weight_Change?: null | number
  Current_Type?: null | 'AC' | 'DC'
  SOC_after_charging?: null | number
  energy_used?: null | number
  stay_duration_seconds?: null | number
  approval_status?: null | 'approved' | 'pending'
}

type Locations_Expected_API_DTO = {
  id: string
  user_created: string
  latitude: number
  longitude: number
  address: string
  name?: string | null
  trip_planning_presets?: Record<string, unknown> | null
}

/**
 * Creates a new favourite location in the database.
 *
 * @param {Omit<Directus_Favourite_Location, "id" | "user_created">} data - The data for the new favourite location.
 * @return {Promise<Directus_Favourite_Location | undefined>} The created favourite location data.
 */
export async function createFavouriteLocation(
  data: Omit<Directus_Favourite_Location, 'id' | 'user_created'>,
): Promise<Directus_Favourite_Location | undefined> {
  const converted_data = locationsConverter.createToApi(data)

  // return await directusCreateOne<Directus_Favourite_Location>(collectionName, data)
  return await createItem<Locations_Expected_API_DTO, Directus_Favourite_Location>(
    collectionName,
    converted_data,
    locationsConverter.apiToLegacy,
  )
}

/**
 * Fetches favourite locations from the database.
 *
 * @return {Promise<Directus_Favourite_Location[] | undefined>} Favourite locations data from the database.
 */
export async function fetchFavouriteLocations(): Promise<
  Directus_Favourite_Location[] | undefined
> {
  return await fetchCollection<Locations_Expected_API_DTO, Directus_Favourite_Location>(
    collectionName,
    undefined,
    locationsConverter.apiToLegacy,
  )
}

/**
 * Updates a favourite location in the database.
 *
 * @param {string} favouriteLocationId - The id of the favourite location to update.
 * @param {Omit<Directus_Favourite_Location, "id" | "user_created">} dataToUpdate - The data to update for the favourite location.
 * @return {Promise<Directus_Favourite_Location | undefined>} The updated favourite location data.
 */
export async function updateFavouriteLocation(
  favouriteLocationId: string,
  dataToUpdate: Partial<Omit<Directus_Favourite_Location, 'id' | 'user_created'>>,
): Promise<Directus_Favourite_Location | undefined> {
  return await updateItem<Directus_Favourite_Location>(
    collectionName,
    favouriteLocationId,
    locationsConverter.updateToApi(dataToUpdate),
    locationsConverter.apiToLegacy,
  )
}

/**
 * Updates a number of favourite locations in the database.
 *
 * @param {string[]} ids - The array of ids of the favourite locations to update.
 * @param {Omit<Directus_Favourite_Location, "id" | "user_created">} dataToUpdate - The data to update for the favourite location.
 * @return {Promise<Directus_Favourite_Location[] | undefined>} The array of updated favourite locations data.
 */
export async function updateMultipleFavouriteLocations(
  ids: string[],
  dataToUpdate: Partial<Omit<Directus_Favourite_Location, 'id' | 'user_created'>>,
): Promise<Directus_Favourite_Location[] | undefined> {
  return await patchMultiple<Directus_Favourite_Location>(
    collectionName,
    ids,
    locationsConverter.updateToApi(dataToUpdate),
    locationsConverter.apiToLegacy,
  )
}

/**
 * Deletes a favourite location from the database.
 *
 * @param {number} favourite_location_directus_id - The directus id of the favourite location to be deleted.
 * @return {Promise<"ok" | "failed">} Either "ok" if successfully deleted or "failed" if not.
 */
export async function deleteFavouriteLocation(
  favourite_location_directus_id: string,
): Promise<'ok' | 'failed'> {
  const res = await deleteItem(collectionName, favourite_location_directus_id)
  return res ? 'ok' : 'failed'
}

const process_trip_planning_presets = (
  passed_data: Partial<Directus_Favourite_Location>,
): Record<string, any> | null | undefined => {
  const array = [
    passed_data.Charge_Here,
    passed_data.Current_Type,
    passed_data.Load_Weight_Change,
    passed_data.Rating,
    passed_data.SOC_after_charging,
    passed_data.energy_used,
  ]
  if (array.every((i) => i === undefined)) return undefined
  const collector_obj: Record<string, any> = {}
  if (passed_data.Charge_Here) collector_obj.charge_here = true
  if (passed_data.Current_Type) collector_obj.current_type = passed_data.Current_Type
  if (passed_data.Load_Weight_Change)
    collector_obj.load_weight_change = passed_data.Load_Weight_Change
  if (passed_data.Rating) collector_obj.rating = passed_data.Rating
  if (passed_data.SOC_after_charging)
    collector_obj.SoC_after_charging = passed_data.SOC_after_charging
  if (passed_data.energy_used) collector_obj.energy_used = passed_data.energy_used
  if (Object.keys(collector_obj).length) return collector_obj
  return null
}

const process_current_type = (passed_data: any): 'AC' | 'DC' | null =>
  passed_data === 'AC' || passed_data === 'DC' ? passed_data : null

const process_coordinates = ({
  latitude,
  longitude,
}: {
  latitude: number
  longitude: number
}): GeoJSON.Point => ({
  coordinates: [longitude, latitude],
  type: 'Point',
})

const process_user_created = (passed_data: string | Partial<DirectusUser>) =>
  typeof passed_data === 'string' ? passed_data : passed_data.id!

export const locationsConverter: CrudConverter<
  Locations_Expected_API_DTO,
  Directus_Favourite_Location,
  Omit<Directus_Favourite_Location, 'id' | 'user_created'>,
  Partial<Omit<Directus_Favourite_Location, 'id' | 'user_created'>>
> = {
  apiToLegacy(api) {
    return {
      Address: api.address,
      Charge_Here: !!api.trip_planning_presets?.charge_here,
      Coordinates: process_coordinates({
        longitude: api.longitude,
        latitude: api.latitude,
      }),
      id: api.id,
      Name: api.name ?? '',
      Type: 'Other',
      user_created: api.user_created,
      Visibility: 'fleet',
      approval_status: null,
      Current_Type: process_current_type(api.trip_planning_presets?.current_type),
      energy_used: process_number_or_null(api.trip_planning_presets?.energy_used),
      Load_Weight_Change: process_number_or_null(api.trip_planning_presets?.load_weight_change),
      Rating: process_number_or_null(api.trip_planning_presets?.rating),
      SOC_after_charging: process_number_or_null(api.trip_planning_presets?.SoC_after_charging),
      stay_duration_seconds: process_number_or_null(
        api.trip_planning_presets?.stay_duration_seconds,
      ),
    }
  },

  legacyToApi(legacy) {
    const [longitude, latitude] = legacy.Coordinates.coordinates as [number, number]
    return {
      address: legacy.Address,
      id: legacy.id.toString(),
      latitude,
      longitude,
      user_created: process_user_created(legacy.user_created),
      name: legacy.Name,
      trip_planning_presets: process_trip_planning_presets(legacy),
    }
  },

  createToApi(data) {
    const [longitude, latitude] = data.Coordinates.coordinates as [number, number]
    return {
      address: data.Address,
      latitude,
      longitude,
      name: data.Name,
      trip_planning_presets: process_trip_planning_presets(data),
    }
  },

  updateToApi(data) {
    const collector_obj: Partial<Omit<Locations_Expected_API_DTO, 'id' | 'user_created'>> = {}
    const processed_trip_planning_data = process_trip_planning_presets(data)
    if (processed_trip_planning_data !== undefined)
      collector_obj.trip_planning_presets = processed_trip_planning_data
    if (data.Address !== undefined) collector_obj.address = data.Address
    if (data.Coordinates !== undefined) {
      const [longitude, latitude] = data.Coordinates.coordinates as [number, number]
      collector_obj.latitude = latitude
      collector_obj.longitude = longitude
    }
    if (data.Name !== undefined) collector_obj.name = data.Name
    return collector_obj
  },
}
